/** Display formatting. Values arrive as integer minor units / UTC ISO strings. */
const thousands = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** Integer cents as "1,234.50" (no currency symbol; negatives keep their sign). */
export function formatAmount(minor: number): string {
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(minor));
  return `${sign}${thousands.format(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, "0")}`;
}

/** Integer cents as "R 1,234.50" ("-R 12.00" when negative). */
export function formatMoney(minor: number): string {
  return `${minor < 0 ? "-" : ""}R ${formatAmount(Math.abs(minor))}`;
}

export function formatPriceHundredths(price: number): string {
  return (price / 100).toFixed(2);
}

export function formatMultiplierHundredths(m: number): string {
  return `${(m / 100).toFixed(2)}×`;
}

export type OddsFormat = "decimal" | "fractional";

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

/** Decimal odds × 100 as a reduced fraction of the winnings, e.g. 250 → "3/2", 200 → "1/1". */
export function formatFractionalHundredths(price: number): string {
  const num = Math.round(price) - 100;
  if (num <= 0) return "0/1";
  const g = gcd(num, 100);
  return `${num / g}/${100 / g}`;
}

/** Odds (decimal × 100) in the player's chosen format. */
export function formatOdds(price: number, format: OddsFormat = "decimal"): string {
  return format === "fractional" ? formatFractionalHundredths(price) : formatPriceHundredths(price);
}

/** Local date-time in the viewer's time zone, e.g. "14 Oct 2026, 19:30". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(new Date(iso));
}

/** "Today 18:45", "Tomorrow 15:00" or "Sat 18 Oct 15:00" in the viewer's time zone. */
export function formatKickoff(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const time = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86_400_000);
  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  const day = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" }).format(d);
  return `${day} ${time}`;
}

export function timeZoneName(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Parses "150", "150.5", "1,500" or "150,50" into cents; returns null when invalid. */
export function parseRandToCents(input: string): number | null {
  let s = input.trim().replace(/\s/g, "").replace(/^R/i, "");
  // "1,500" / "1,500.50": thousands separators; "150,50": decimal comma.
  if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s)) s = s.replace(/,/g, "");
  else s = s.replace(",", ".");
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(s)) return null;
  const [w, f = ""] = s.split(".");
  return Number(w) * 100 + Number((f + "00").slice(0, 2));
}

export function centsToRandInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function humanizeCode(code: string): string {
  return code.replace(/[_.]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
